import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScaleAgent } from '../hooks/useScaleAgent';
import { WeightDataType } from '../types/scaleAgent';
import { useAuth } from '../hooks/useAuth';

interface WeightCapturePropsType {
  /** ID du site actuel */
  siteId?: string;
  /** ID du pont bascule sélectionné */
  pontBasculeId?: string;
  /** Callback appelé quand un poids stable est capturé */
  onWeightCaptured?: (weight: WeightDataType) => void;
  /** Callback appelé quand le statut change */
  onStatusChange?: (status: 'connected' | 'disconnected' | 'error' | 'checking') => void;
  /** Callback appelé quand le poids change (pour mise à jour en temps réel) */
  onWeightChange?: (weight: number) => void;
  /** Callback appelé quand le pont bascule change */
  onPontBasculeChange?: (pontBasculeId: string) => void;
  /** Mode de fonctionnement */
  mode?: 'gross' | 'tare' | 'auto';
  /** Désactiver la capture */
  disabled?: boolean;
  /** Classe CSS personnalisée */
  className?: string;
  /** Poids brut pour le calcul du poids net (en mode tare) */
  grossWeight?: number | string;
  /** Poids des sacs calculé (en mode tare) - utilise le poids calculé depuis QualityAnalysisSection */
  calculatedBagWeight?: number | string;
  /** Callback appelé pour sauvegarder complètement le poids tare (étape 3) */
  onSaveTareWeight?: (tareWeight: number) => Promise<void>;
  /** Indique si un poids est déjà enregistré (pour désactiver la capture automatique) */
  hasExistingWeight?: boolean;
  /** Poids existant à afficher quand hasExistingWeight est true */
  existingWeight?: number | string;
}

/**
 * Composant de capture de poids avec sélection de pont bascule intégrée
 */
export const WeightCapture: React.FC<WeightCapturePropsType> = ({
  siteId,
  pontBasculeId,
  onWeightCaptured,
  onStatusChange,
  onWeightChange,
  onPontBasculeChange,
  mode = 'auto',
  disabled = false,
  className = '',
  grossWeight,
  calculatedBagWeight,
  onSaveTareWeight,
  hasExistingWeight = false,
  existingWeight
}) => {
  // Hook Auth pour récupérer les ponts bascule de l'utilisateur
  const { user, getAllPontsBascule } = useAuth();
  
  // États locaux pour la sélection de pont bascule
  const [availablePontsBascule, setAvailablePontsBascule] = useState<any[]>([]);
  const [selectedPontBascule, setSelectedPontBascule] = useState<any>(null);
  const [pontsLoading, setPontsLoading] = useState(false);
  const [showPontBasculeSelector, setShowPontBasculeSelector] = useState(false);

  // États pour la connectivité
  const [connectivityStatus, setConnectivityStatus] = useState<'checking' | 'connected' | 'disconnected' | 'error'>('checking');
  const [connectivityError, setConnectivityError] = useState<string>('');
  const [lastHealthCheck, setLastHealthCheck] = useState<Date | null>(null);
  const [isCheckingConnectivity, setIsCheckingConnectivity] = useState(false);
  const connectivityCheckRef = useRef<NodeJS.Timeout | null>(null);
  const hasAttemptedConnection = useRef(false);

  // États locaux pour la gestion du poids et de la connexion
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [weightData, setWeightData] = useState<any>(null);
  
  // État pour le modal de confirmation
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [lastCapturedWeight, setLastCapturedWeight] = useState<any>(null);
  
  // État pour la gestion des erreurs
  const [captureError, setCaptureError] = useState<string | null>(null);
  
  // État pour gérer l'affichage du poids enregistré vs temps réel
  const [showRealTimeWeight, setShowRealTimeWeight] = useState(false);
  const [registeredWeight, setRegisteredWeight] = useState<any>(null);

  // Utiliser le hook useScaleAgent pour la connexion en temps réel
  const {
    isConnected: scaleAgentConnected,
    isConnecting: scaleAgentConnecting,
    currentWeight: scaleAgentWeight,
    status: scaleAgentStatus,
    error: scaleAgentError,
    pontsBascule: scaleAgentPonts,
    selectedPontBascule: scaleAgentSelectedPont,
    connect: scaleAgentConnect
  } = useScaleAgent(siteId, pontBasculeId);

  // Fonction pour envoyer des requêtes HTTP au Scale Agent avec API key
  const sendScaleAgentRequest = useCallback(async (endpoint: string, method: 'GET' | 'POST' = 'GET', body?: any) => {
  
    if (!selectedPontBascule) {
      throw new Error('Aucun pont bascule sélectionné');
    }
    
    if (!selectedPontBascule.apiKey) {
      throw new Error(`Pont ${selectedPontBascule.name} n'a pas de clé API configurée`);
    }
    
    try {
      const options: RequestInit = {
        method,
        headers: {
          'x-api-key': selectedPontBascule.apiKey
        }
      };

      if (body) {
        options.headers = {
          ...options.headers,
          'Content-Type': 'application/json'
        };
        options.body = JSON.stringify(body);
      }

      // Le Scale Agent est accessible uniquement sur localhost
      const baseUrl = '127.0.0.1';
      const port = selectedPontBascule.port || 9090;
      const fullUrl = `http://${baseUrl}:${port}${endpoint}`;
      const response = await fetch(fullUrl, options);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Scale Agent request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      return result;
     } catch (error) {
       throw error;
     }
  }, [selectedPontBascule?.id, selectedPontBascule?.apiKey]); // Optimisé les dépendances

  // Synchroniser avec le pont sélectionné par le hook useScaleAgent
  useEffect(() => {
    if (scaleAgentSelectedPont && scaleAgentSelectedPont !== selectedPontBascule) {
      setSelectedPontBascule(scaleAgentSelectedPont);
    }
  }, [scaleAgentSelectedPont, selectedPontBascule]);

  // Vérifier la connectivité via les endpoints HTTP
  const checkConnectivity = useCallback(async () => {
    if (!selectedPontBascule || isCheckingConnectivity) return false; 
    try {
      setIsCheckingConnectivity(true);
      setConnectivityStatus('checking');
      
      // Vérifier l'endpoint /health
      await sendScaleAgentRequest('/health');
      
      // Vérifier l'endpoint /weigh/read (POST avec body)
      await sendScaleAgentRequest('/weigh/read', 'POST', {
        ticket_ref: "TEST-001"
      });
      
      // Si les deux endpoints répondent, le pont est connecté
      setConnectivityStatus('connected');
      setConnectivityError('');
      setLastHealthCheck(new Date());
      
      // Notifier le composant parent du statut de connectivité
      if (onStatusChange) {
        onStatusChange('connected');
      }
      
      return true;
      
    } catch (error) {
      setConnectivityStatus('error');
      setConnectivityError(`Erreur de connectivité: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      
      // Notifier le composant parent du statut d'erreur
      if (onStatusChange) {
        onStatusChange('error');
      }
      
      return false;
    } finally {
      setIsCheckingConnectivity(false);
    }
  }, [selectedPontBascule?.id, sendScaleAgentRequest, isCheckingConnectivity]); // Optimisé les dépendances

  // Synchroniser avec les ponts du hook useScaleAgent
  useEffect(() => {
    if (scaleAgentPonts && scaleAgentPonts.length > 0) {
      setAvailablePontsBascule(scaleAgentPonts);
      
      // Ne synchroniser que si aucun pont n'est sélectionné localement
      if (!selectedPontBascule && scaleAgentSelectedPont) {
        setSelectedPontBascule(scaleAgentSelectedPont);
        hasAttemptedConnection.current = false; // Reset du flag pour le nouveau pont
      }
    }
  }, [scaleAgentPonts?.length, scaleAgentSelectedPont?.id]); // Supprimé selectedPontBascule?.id des dépendances

    // Synchroniser weightData avec les données du Scale Agent
   useEffect(() => {
     if (scaleAgentWeight && scaleAgentWeight.raw) {
       setWeightData(scaleAgentWeight);
       
       // Notifier le composant parent du changement de poids
       if (onWeightChange) {
         // Extraire le nombre du format "789.82 kg"
         const weightValue = parseFloat(scaleAgentWeight.raw);
         if (!isNaN(weightValue)) {
           // Arrondir le poids à 2 décimales pour respecter la contrainte du modèle
           const roundedWeight = Math.round(weightValue * 100) / 100;
           onWeightChange(roundedWeight);
         } else {
           console.log('🔍 WeightCapture - Impossible de parser le poids depuis raw:', scaleAgentWeight.raw);
         }
       }
     }
   }, [scaleAgentWeight, onWeightChange]);

    // Synchroniser l'état de connexion avec le Scale Agent
   useEffect(() => {
     setIsConnected(scaleAgentConnected);
     setIsConnecting(scaleAgentConnecting);
     
     // Notifier le composant parent du changement de statut
     if (onStatusChange) {
       if (scaleAgentError) {
         onStatusChange('error');
       } else if (scaleAgentConnected) {
         onStatusChange('connected');
       } else if (scaleAgentConnecting) {
         onStatusChange('checking');
       } else {
         onStatusChange('disconnected');
       }
     }
     
     // Ne pas changer le statut de connectivité ici, laissons checkConnectivity gérer cela
     // car elle vérifie les vrais endpoints HTTP
   }, [scaleAgentConnected, scaleAgentConnecting, scaleAgentError, scaleAgentStatus, onStatusChange]);

    // Charger les ponts bascule disponibles - UNE SEULE FOIS au montage
  useEffect(() => {
    const loadPontsBascule = async () => {
      if (!user) return;
      
      try {
        setPontsLoading(true);
        
        if (user.has_sites && user.sites && user.sites.length > 0) {
          // Utiliser les ponts des sites de l'utilisateur
          const ponts = getAllPontsBascule();
          setAvailablePontsBascule(ponts);
          
          // Sélectionner automatiquement le pont par défaut
          const pontDefault = ponts.find(pont => pont.is_default);
          if (pontDefault) {
            setSelectedPontBascule(pontDefault);
            hasAttemptedConnection.current = false; // Reset du flag pour le nouveau pont
            
            // Ne pas vérifier la connectivité ici, laissons l'effet de connexion s'en charger
            // pour éviter les boucles infinies
          }
        } else {
          // L'utilisateur n'a pas de site, utiliser les ponts du hook useScaleAgent
          // Ne pas charger de ponts ici, le hook useScaleAgent s'en chargera
          // Les ponts seront synchronisés via l'effet de synchronisation
        }
      } catch (error) {
        // Gestion silencieuse de l'erreur
      } finally {
        setPontsLoading(false);
      }
    };

    loadPontsBascule();
  }, [user?.id]); // Retirer getAllPontsBascule des dépendances pour éviter la boucle infinie

  // Fonctions utilitaires
  const getFormattedWeight = useCallback(() => {
    // Si un poids est déjà enregistré et qu'on n'affiche pas le temps réel, afficher le poids enregistré
    if (hasExistingWeight && !showRealTimeWeight) {
      // Priorité au poids existant passé en paramètre
      if (existingWeight) {
        return `${existingWeight} kg`;
      }
      // Fallback sur le poids enregistré précédemment
      if (registeredWeight) {
        return registeredWeight.raw || `${registeredWeight.gross_weight || 0} ${registeredWeight.unit || 'kg'}`;
      }
    }
    
    // Sinon, afficher le poids en temps réel
    if (!weightData) return '--';
    
    // Priorité à la propriété raw qui contient le format exact
    if (weightData.raw) {
      return weightData.raw;
    }
    // Fallback sur gross_weight + unit
    return `${weightData.gross_weight || 0} ${weightData.unit || 'kg'}`;
  }, [weightData, hasExistingWeight, showRealTimeWeight, registeredWeight, existingWeight]);

  const isWeightStable = useCallback(() => {
    // Si un poids est déjà enregistré et qu'on n'affiche pas le temps réel, considérer comme stable
    if (hasExistingWeight && !showRealTimeWeight) {
      return true;
    }
    
    if (!weightData) return false;
    return weightData.status === 'OK';
  }, [weightData, hasExistingWeight, showRealTimeWeight]);

  const connect = useCallback(async () => {
         if (!selectedPontBascule) {
       return;
     }

    try {
      setIsConnecting(true);
      
        // Utiliser la configuration du pont bascule pour se connecter
        // Séparer les ports HTTP et WebSocket
        const config = {
          pontBasculeId: selectedPontBascule.id,
          pontBasculeName: selectedPontBascule.name,
          siteId: siteId || selectedPontBascule.site_id,
          wsUrl: selectedPontBascule.wsUrl || `ws://127.0.0.1:9091/weigh/stream`, // Port 9091 pour WebSocket
          port: selectedPontBascule.port_ws || 9091, // Port WebSocket séparé
          reconnect: {
            enabled: true,
            maxAttempts: 5,
            delay: 1000,
          },
          scale: {
            unit: selectedPontBascule.scale_unit || 'kg',
            precision: selectedPontBascule.scale_precision || 2,
            stabilityThreshold: selectedPontBascule.stability_threshold || 0.01,
          },
        };

        

        await scaleAgentConnect(config);
      
         } catch (error) {
       setConnectivityError(`Erreur de connexion: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
     } finally {
      setIsConnecting(false);
    }
  }, [selectedPontBascule, siteId, scaleAgentConnect]);


     const refreshWeight = useCallback(async () => {
      if (!selectedPontBascule) {
        return;
      }

      try {
        // Activer l'affichage du poids en temps réel
        setShowRealTimeWeight(true);
        
        // Utiliser l'endpoint /weigh/read pour rafraîchir le poids
        await sendScaleAgentRequest('/weigh/read', 'POST', {
          action: 'refresh',
          ticket_ref: 'REFRESH-' + Date.now()
        });
      } catch (error: any) {
        setConnectivityError(`Erreur de rafraîchissement: ${error.message || 'Erreur inconnue'}`);
      }
   }, [selectedPontBascule, sendScaleAgentRequest]);

   

      const tare = useCallback(async () => {
      if (!selectedPontBascule) {
        return;
      }

      try {
        // Utiliser l'endpoint /weigh/read pour la tare
        await sendScaleAgentRequest('/weigh/read', 'POST', {
          action: 'tare',
          ticket_ref: 'TARE-' + Date.now()
        });
      } catch (error: any) {
        setConnectivityError(`Erreur de tare: ${error.message || 'Erreur inconnue'}`);
      }
    }, [selectedPontBascule, sendScaleAgentRequest]);

      const calibrate = useCallback(async () => {
      if (!selectedPontBascule) {
        return;
      }

      try {
        // Note: L'endpoint /calibrate n'existe pas sur le Scale Agent
        // Utiliser l'endpoint /weigh/read avec une commande de calibration
        await sendScaleAgentRequest('/weigh/read', 'POST', {
          action: 'calibrate',
          ticket_ref: 'CALIBRATE-' + Date.now()
        });
      } catch (error: any) {
        setConnectivityError(`Erreur de calibration: ${error.message || 'Erreur inconnue'}`);
      }
    }, [selectedPontBascule, sendScaleAgentRequest]);

  // Gérer la sélection d'un pont bascule
  const handlePontBasculeSelect = (pont: any) => {
    setSelectedPontBascule(pont);
    setShowPontBasculeSelector(false);
    
    // Notifier le composant parent du changement de pont bascule
    if (onPontBasculeChange && pont?.id) {
      onPontBasculeChange(pont.id.toString());
    }
  };

  // Effet pour la connexion automatique - UNE SEULE FOIS au montage
  useEffect(() => {
    // Ne se déclenche qu'une seule fois par pont bascule
    if (selectedPontBascule && !hasAttemptedConnection.current && !isConnected && !isConnecting && !disabled) {
      hasAttemptedConnection.current = true;
      
             // Vérifier que le pont bascule a une clé API avant de tester la connectivité
       if (!selectedPontBascule.apiKey) {
         setConnectivityStatus('error');
         setConnectivityError('Pont bascule sans clé API configurée');
         return;
       }
      
      // Utiliser un timeout pour éviter les appels immédiats multiples
      if (connectivityCheckRef.current) {
        clearTimeout(connectivityCheckRef.current);
      }
      
      connectivityCheckRef.current = setTimeout(async () => {
                 try {
           const isConnected = await checkConnectivity();
           if (isConnected) {
             await connect();
           }
         } catch (error) {
           // Gestion silencieuse de l'erreur
         }
      }, 500);
    }
    
    // Nettoyage des timeouts au démontage
    return () => {
      if (connectivityCheckRef.current) {
        clearTimeout(connectivityCheckRef.current);
        connectivityCheckRef.current = null;
      }
    };
  }, [selectedPontBascule?.id]); // SEULEMENT l'ID du pont bascule

  // Nettoyage au démontage du composant
  useEffect(() => {
    return () => {
      hasAttemptedConnection.current = false;
      if (connectivityCheckRef.current) {
        clearTimeout(connectivityCheckRef.current);
        connectivityCheckRef.current = null;
      }
    };
  }, []);

  // Gérer la capture automatique
  const handleAutoCapture = useCallback((weight: WeightDataType) => {
    if (weight.stable && weight.status === 'OK') {
      onWeightCaptured?.(weight);
    }
  }, [onWeightCaptured]);

  // Effet pour la capture automatique en mode auto
  useEffect(() => {
    if (mode === 'auto' && weightData && isWeightStable() && onWeightCaptured && !hasExistingWeight) {
      // Arrondir le poids à 2 décimales pour respecter la contrainte du modèle
      const rawWeight = weightData.gross_weight || 0;
      const roundedWeight = Math.round(rawWeight * 100) / 100;
      
      const weightToCapture = {
        weight: roundedWeight,
        gross_weight: roundedWeight,
        unit: weightData.unit || 'kg',
        stable: true,
        status: weightData.status || 'OK',
        raw: weightData.raw || '',
        timestamp: weightData.measure_ts || new Date().toISOString(),
        device: weightData.device_id || 'unknown'
      };
      
      handleAutoCapture(weightToCapture);
    }
  }, [weightData, isWeightStable, mode, handleAutoCapture, onWeightCaptured, hasExistingWeight]);

  // Effet pour initialiser le poids enregistré quand hasExistingWeight devient true
  useEffect(() => {
    if (hasExistingWeight && weightData && !registeredWeight) {
      setRegisteredWeight(weightData);
      setShowRealTimeWeight(false);
    }
  }, [hasExistingWeight, weightData, registeredWeight]);

    // Capturer manuellement le poids actuel
  const handleManualCapture = useCallback(async () => {
    // Si un poids est déjà enregistré et qu'on n'affiche pas le temps réel, utiliser le poids enregistré
    let currentWeightData;
    if (hasExistingWeight && !showRealTimeWeight) {
      // Utiliser le poids existant passé en paramètre ou le poids enregistré
      if (existingWeight) {
        currentWeightData = { gross_weight: parseFloat(existingWeight.toString()), unit: 'kg' };
      } else if (registeredWeight) {
        currentWeightData = registeredWeight;
      }
    } else {
      currentWeightData = weightData;
    }
    
    if (!currentWeightData || !isWeightStable()) {
      return;
    }

    try {
      // Arrondir le poids à 2 décimales pour respecter la contrainte du modèle
      const rawWeight = currentWeightData.gross_weight || 0;
      const roundedWeight = Math.round(rawWeight * 100) / 100;
      
      const weightToCapture = {
        weight: roundedWeight,
        gross_weight: roundedWeight,
        unit: currentWeightData.unit || 'kg',
        stable: true,
        status: currentWeightData.status || 'OK',
        raw: currentWeightData.raw || '',
        timestamp: currentWeightData.measure_ts || new Date().toISOString(),
        device: currentWeightData.device_id || 'unknown'
      };
      
      // Enregistrer le poids via le callback parent
      if (mode === 'tare' && onSaveTareWeight) {
        // Mode tare : utiliser le callback de sauvegarde complète
        await onSaveTareWeight(roundedWeight);
        // Ne pas afficher le modal de confirmation en mode tare
        // Le modal de récapitulatif sera géré par le composant parent
      } else if (onWeightCaptured) {
        // Mode brut : utiliser le callback standard
        onWeightCaptured(weightToCapture);
        setLastCapturedWeight(weightToCapture);
        setShowConfirmationModal(true);
      }
      
    } catch (error: any) {
      // Gestion des erreurs de capture
      console.error('❌ Erreur lors de la capture du poids:', error);
      setCaptureError('Erreur lors de la capture du poids. Veuillez réessayer.');
      // Auto-masquer l'erreur après 8 secondes
      setTimeout(() => setCaptureError(null), 8000);
    }
  }, [weightData, isWeightStable, onWeightCaptured, hasExistingWeight, showRealTimeWeight, registeredWeight, mode, onSaveTareWeight]);

  // Fonction pour continuer vers l'étape suivante
  const handleContinue = useCallback(() => {
    setShowConfirmationModal(false);
    // Le composant parent gère la navigation vers l'étape suivante
    // via le workflow existant
  }, []);

  // Fonction pour mettre en attente
  const handleWait = useCallback(() => {
    setShowConfirmationModal(false);
    // La pesée est déjà en attente par défaut
    // L'utilisateur peut la reprendre plus tard
  }, []);

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      {/* 1. EN-TÊTE : Type de pesée, nom du pont, bouton de sélection */}
      <div className="flex items-center justify-between mb-0">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'gross' ? 'Poids Brut' : mode === 'tare' ? 'Poids Tare' : 'Poids'}
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Pont:</span>
            <span className="font-medium text-blue-600">
              {selectedPontBascule?.name || 'Non sélectionné'}
            </span>
            {selectedPontBascule?.is_default && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                Par défaut
              </span>
            )}
            {/* État de connexion sur la même ligne */}
            {selectedPontBascule && (
              <div className="flex items-center space-x-1 ml-2">
                <div className={`w-2 h-2 rounded-full ${
                  connectivityStatus === 'connected' ? 'bg-green-500' : 
                  connectivityStatus === 'checking' ? 'bg-yellow-500' : 
                  connectivityStatus === 'error' ? 'bg-red-500' : 'bg-gray-400'
                }`}></div>
                <span className={`text-xs ${
                  connectivityStatus === 'connected' ? 'text-green-600' : 
                  connectivityStatus === 'checking' ? 'text-yellow-600' : 
                  connectivityStatus === 'error' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {connectivityStatus === 'connected' ? 'Connecté' : 
                   connectivityStatus === 'checking' ? 'Vérification...' : 
                   connectivityStatus === 'error' ? 'Erreur' : 'Déconnecté'}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Bouton de sélection de pont */}
        <button
          onClick={() => setShowPontBasculeSelector(!showPontBasculeSelector)}
          className="p-2 bg-gray-200 text-gray-600 hover:text-blue-600 hover:bg-gray-300 rounded-lg transition-colors"
          title="Sélectionner un autre pont bascule"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

             {/* Date de dernière vérification et bouton de re-vérification */}
       {selectedPontBascule && (
         <div className="flex items-center space-x-3 mb-4 p-2">
           <div className="text-xs text-gray-500">
             Dernière vérification: {lastHealthCheck ? lastHealthCheck.toLocaleTimeString('fr-FR') : 'Jamais'}
           </div>
                       <button
              onClick={checkConnectivity}
              disabled={isCheckingConnectivity || connectivityStatus === 'checking'}
              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 transition-colors"
            >
              {isCheckingConnectivity || connectivityStatus === 'checking' ? 'Vérification...' : 'Re-vérifier'}
            </button>
         </div>
       )}

      {/* Sélecteur de pont bascule */}
      {showPontBasculeSelector && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sélectionner un pont bascule:
          </label>
          {pontsLoading ? (
            <div className="text-sm text-gray-500">Chargement des ponts bascule...</div>
          ) : (
            <select
              value={selectedPontBascule?.id || ''}
              onChange={(e) => {
                const pont = availablePontsBascule.find(p => p.id === e.target.value);
                handlePontBasculeSelect(pont);
              }}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Sélectionner un pont bascule</option>
              {availablePontsBascule.map((pont) => (
                <option key={pont.id} value={pont.id}>
                  {pont.name} - {pont.location} {pont.is_default && '(Par défaut)'}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

                           {/* Affichage du poids actuel */}
        <div className="text-center mb-8">
          <div className="text-8xl font-bold text-gray-900 mb-4">
            {weightData ? getFormattedWeight() : '--'}
          </div>
          <div className="flex items-center justify-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${
              weightData && isWeightStable() ? 'bg-green-500' : 'bg-yellow-500'
            }`}></div>
            <span className={`text-lg font-medium ${
              weightData && isWeightStable() ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {hasExistingWeight && !showRealTimeWeight ? 'Poids enregistré' : 
               weightData ? (isWeightStable() ? 'Stable' : 'Instable') : 'Aucun poids'}
            </span>
            {hasExistingWeight && !showRealTimeWeight && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                Enregistré
              </span>
            )}
            {showRealTimeWeight && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                Temps réel
              </span>
            )}
          </div>
        </div>

        {/* Section calcul du poids net - uniquement en mode tare */}
        {mode === 'tare' && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-xs text-gray-600 mb-1">Poids Brut</p>
                <p className="text-sm font-bold text-green-600">
                  {grossWeight ? `${parseFloat(grossWeight.toString()).toLocaleString()} kg` : 'N/A'}
                </p>
              </div>
              <div className="flex items-center justify-center">
                <span className="text-lg text-gray-400">−</span>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Poids Tare</p>
                <p className="text-sm font-bold text-blue-600">
                  {weightData?.gross_weight ? `${parseFloat(weightData.gross_weight.toString()).toLocaleString()} kg` : 'En attente...'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Poids Sacs</p>
                <p className="text-sm font-bold text-purple-600">
                  {calculatedBagWeight ? `${parseFloat(calculatedBagWeight.toString()).toLocaleString()} kg` : '0 kg'}
                </p>
              </div>
            </div>
            <div className="border-t border-green-300 pt-3 mt-3 text-center">
              <p className="text-xs text-gray-600 mb-1">Poids Net</p>
              <p className="text-3xl font-bold text-green-700">
                {grossWeight && weightData?.gross_weight ? 
                  `${(parseFloat(grossWeight.toString()) - parseFloat(weightData.gross_weight.toString()) - (calculatedBagWeight ? parseFloat(calculatedBagWeight.toString()) : 0)).toLocaleString()} kg` : 
                  'Calcul en cours...'
                }
              </p>
              <div className="text-xs text-gray-500 mt-1">
                <p>Formule: Poids Brut - (Poids Tare + Poids Sacs)</p>
                {weightData?.gross_weight && (
                  <p className="text-green-500 mt-1">
                    <i className="fas fa-sync-alt mr-1 animate-spin"></i>
                    Mise à jour automatique
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

                                                                               {/* Boutons d'action */}
        <div className="bg-gray-100 p-6 -mx-6 -mb-6">
         <div className="flex space-x-3 items-center justify-center">
           {/* Calibrer */}
           <button
             onClick={calibrate}
             disabled={!selectedPontBascule || connectivityStatus !== 'connected'}
             className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
             title="Calibrer la balance"
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
             </svg>
           </button>

           {/* Rafraîchir */}
           <button
             onClick={refreshWeight}
             disabled={!selectedPontBascule || connectivityStatus !== 'connected'}
             className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
             title="Rafraîchir le poids"
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
             </svg>
           </button>

           {/* Tare */}
           <button
             onClick={tare}
             disabled={!selectedPontBascule || connectivityStatus !== 'connected'}
             className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
             title="Mettre la balance à zéro"
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3z" />
             </svg>
           </button>

           {/* Enregistrer le poids - Bouton principal à droite */}
           <button
             onClick={handleManualCapture}
             disabled={!weightData || !isWeightStable() || connectivityStatus !== 'connected' || (hasExistingWeight && !showRealTimeWeight)}
             className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
             title={mode === 'tare' ? 'Enregistrer le poids tare' : 'Enregistrer le poids brut'}
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
             </svg>
             <span>{mode === 'tare' ? 'Enregistrer le poids tare' : 'Enregistrer le poids brut'}</span>
           </button>
         </div>
       </div>



      {/* Messages d'information essentiels */}
      {!selectedPontBascule && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-yellow-800 text-sm">Aucun pont bascule sélectionné</div>
        </div>
      )}

      {hasExistingWeight && !showRealTimeWeight && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-info-circle text-blue-600 mr-2"></i>
            <div className="text-blue-800 text-sm">
              Un poids est déjà enregistré. Cliquez sur "Rafraîchir le poids" pour capturer un nouveau poids.
            </div>
          </div>
        </div>
      )}

      {selectedPontBascule && connectivityStatus !== 'connected' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-800 text-sm">
            Pont {selectedPontBascule.name} non connecté
            {connectivityError && (
              <div className="mt-1 text-xs text-red-600">
                {connectivityError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message d'erreur de capture */}
      {captureError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
            <div className="text-red-800 text-sm">
              {captureError}
            </div>
            <button
              onClick={() => setCaptureError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

              {/* Modal de confirmation - uniquement en mode brut */}
        {mode !== 'tare' && (
          <ConfirmationModal
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
            onContinue={handleContinue}
            onWait={handleWait}
            weightData={lastCapturedWeight}
          />
        )}
      </div>
    );
  };

// Composant modal pour la confirmation après enregistrement
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onWait: () => void;
  weightData: any;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  onWait,
  weightData
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Poids brut enregistré avec succès !
          </h3>
          
          <p className="text-sm text-gray-600 mb-4">
            Le poids de <strong>{weightData?.weight || weightData?.gross_weight || weightData?.raw || 'N/A'} kg</strong> a été enregistré.
          </p>
          
          <div className="text-sm text-gray-500 mb-6 p-3 bg-gray-50 rounded-lg">
            <p className="font-medium mb-2">Informations enregistrées :</p>
            <ul className="text-left space-y-1">
              <li>• Type de pesée</li>
              <li>• Produit sélectionné</li>
              <li>• Véhicule et chauffeur</li>
              <li>• Fournisseur</li>
              <li>• Poids brut</li>
            </ul>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onWait}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Mettre en attente
            </button>
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Continuer
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="mt-3 text-sm text-gray-500 hover:text-gray-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeightCapture;

